import { useEffect, type ComponentPropsWithoutRef, type ReactNode } from "react";
import {
  FormProvider,
  useForm,
  type DefaultValues,
  type FieldValues,
  type Resolver,
  type SubmitHandler,
  type UseFormProps,
  type UseFormReturn,
} from "react-hook-form";

import { handleEnterAsTabNavigation } from "@/shared/utils/keyboardNavigation";

type CommonFormChildren<TFieldValues extends FieldValues> =
  | ReactNode
  | ((methods: UseFormReturn<TFieldValues>) => ReactNode);

type CommonFormProps<TFieldValues extends FieldValues> = {
  children: CommonFormChildren<TFieldValues>;
  className?: string;
  defaultValues?: DefaultValues<TFieldValues>;
  formProps?: Omit<ComponentPropsWithoutRef<"form">, "children" | "className" | "onSubmit">;
  mode?: UseFormProps<TFieldValues>["mode"];
  onSubmit: SubmitHandler<TFieldValues>;
  reValidateMode?: UseFormProps<TFieldValues>["reValidateMode"];
  resolver?: Resolver<TFieldValues>;
  resetOnDefaultValuesChange?: boolean;
};

export function CommonForm<TFieldValues extends FieldValues>({
  children,
  className,
  defaultValues,
  formProps,
  mode = "onSubmit",
  onSubmit,
  reValidateMode,
  resolver,
  resetOnDefaultValuesChange = true,
}: CommonFormProps<TFieldValues>) {
  const methods = useForm<TFieldValues>({
    defaultValues,
    mode,
    reValidateMode,
    resolver,
  });

  useEffect(() => {
    if (!resetOnDefaultValuesChange || !defaultValues) return;
    methods.reset(defaultValues);
  }, [defaultValues, methods, resetOnDefaultValuesChange]);

  return (
    <FormProvider {...methods}>
      <form
        {...formProps}
        className={className}
        onSubmit={methods.handleSubmit(onSubmit)}
        onKeyDown={(event) => {
          formProps?.onKeyDown?.(event);
          handleEnterAsTabNavigation(event);
        }}
      >
        {typeof children === "function" ? children(methods) : children}
      </form>
    </FormProvider>
  );
}
